import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Select, Switch } from 'antd';
import Circular from '../Loader/Circular';
import ScrollEndMessage from '../ScrollEndMessage';
import useColors from '../../hooks/useColors';
import LabelExpandContext from './LabelExpandContext';
import './styles.css';

/**
 * A dropdown that loads its options page by page while the user scrolls, keeps a
 * search box on top of it, and tells the user when there is nothing left to load.
 *
 * Two modes:
 *  - remote: pass `fetchPage({ page, search })`, it must resolve to
 *    `{ options, hasMore }`. Searching re-queries the server from page 1.
 *  - local: pass `items` (a plain option array). It is filtered in-memory and
 *    revealed `pageSize` at a time while scrolling.
 *
 * Options may carry a `searchText` string when their `label` is a React node —
 * local filtering falls back to it.
 */
const PaginatedSelect = React.forwardRef(({
    // remote mode
    fetchPage,
    enabled = true,
    debounce = 400,
    // local mode
    items,
    // shared
    pageSize = 25,
    extraOptions = [],
    endMessage = 'All records fetched, no further data',
    onSearchChange,
    // adds a switch inside the dropdown that un-truncates long option labels
    showFullLabelToggle = false,
    fullLabelToggleText = 'Show full names',
    ...selectProps
}, ref) => {
    const { colorPrimary } = useColors();
    const isRemote = typeof fetchPage === 'function';

    const [expandLabels, setExpandLabels] = useState(false);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    // remote state
    const [remoteOptions, setRemoteOptions] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMoreRemote, setHasMoreRemote] = useState(true);
    const [loading, setLoading] = useState(false);
    // only the latest request is allowed to write into state, so a slow page-1
    // response cannot overwrite a newer search
    const requestIdRef = useRef(0);

    // local state
    const [visibleCount, setVisibleCount] = useState(pageSize);

    // debounce the typed search
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), debounce);
        return () => clearTimeout(timer);
    }, [search, debounce]);

    useEffect(() => {
        onSearchChange && onSearchChange(debouncedSearch);
    }, [debouncedSearch]);

    const loadPage = useCallback(
        async (pageNo, searchTerm) => {
            if (!isRemote) return;
            const requestId = ++requestIdRef.current;
            setLoading(true);
            try {
                const res = await fetchPage({
                    page: pageNo,
                    search: searchTerm,
                });
                if (requestId !== requestIdRef.current) return; // stale
                const newOptions = res?.options || [];
                setRemoteOptions((prev) =>
                    pageNo === 1 ? newOptions : [...prev, ...newOptions],
                );
                setHasMoreRemote(
                    res?.hasMore !== undefined
                        ? !!res.hasMore
                        : newOptions.length > 0,
                );
                setPage(pageNo);
            } catch (err) {
                if (requestId !== requestIdRef.current) return;
                setHasMoreRemote(false);
            } finally {
                if (requestId === requestIdRef.current) setLoading(false);
            }
        },
        [fetchPage, isRemote],
    );

    // first load + every time the (debounced) search changes
    useEffect(() => {
        if (!isRemote || !enabled) return;
        loadPage(1, debouncedSearch);
    }, [isRemote, enabled, debouncedSearch]);

    // local mode: filter in memory
    const filteredLocal = useMemo(() => {
        const list = items || [];
        const term = search.trim().toLowerCase();
        if (!term) return list;
        return list.filter((opt) => {
            const text =
                opt?.searchText ??
                (typeof opt?.label === 'string' ? opt.label : '');
            return String(text).toLowerCase().includes(term);
        });
    }, [items, search]);

    // Reset how much of a local list is revealed when the list itself or the
    // search changes. Keyed on the length rather than the array identity:
    // callers rebuild the array on every render, and resetting on identity would
    // snap an open dropdown back to the first chunk.
    useEffect(() => {
        if (isRemote) return;
        setVisibleCount(pageSize);
    }, [isRemote, search, items?.length, pageSize]);

    const displayedOptions = useMemo(() => {
        const base = isRemote
            ? remoteOptions
            : filteredLocal.slice(0, visibleCount);
        const known = new Set(base.map((o) => o.value));

        // the selected value(s) must keep their label even when they are not on
        // a loaded page / were filtered out by the current search
        const selected = Array.isArray(selectProps.value)
            ? selectProps.value
            : selectProps.value === undefined ||
                selectProps.value === null ||
                selectProps.value === ''
                ? []
                : [selectProps.value];
        const pool = [...(extraOptions || []), ...(isRemote ? [] : items || [])];
        const missing = [];
        selected.forEach((val) => {
            if (known.has(val)) return;
            const option = pool.find((o) => o && o.value === val);
            if (option) {
                known.add(val);
                missing.push(option);
            }
        });
        return missing.length ? [...missing, ...base] : base;
    }, [
        isRemote,
        remoteOptions,
        filteredLocal,
        visibleCount,
        extraOptions,
        items,
        selectProps.value,
    ]);

    const hasMore = isRemote
        ? hasMoreRemote
        : visibleCount < filteredLocal.length;

    const onPopupScroll = (e) => {
        const target = e.target;
        const reachedBottom =
            target.scrollTop + target.offsetHeight + 12 >= target.scrollHeight;
        if (!reachedBottom || !hasMore) return;
        if (isRemote) {
            if (loading) return;
            loadPage(page + 1, debouncedSearch);
        } else {
            setVisibleCount((prev) => prev + pageSize);
        }
    };

    const onDropdownVisibleChange = (open) => {
        if (!open && search) setSearch('');
        selectProps.onDropdownVisibleChange &&
            selectProps.onDropdownVisibleChange(open);
    };

    // `searchValue` is controlled here, so the typed text has to be cleared by
    // hand once a value is picked
    const onChange = (...args) => {
        if (search) setSearch('');
        selectProps.onChange && selectProps.onChange(...args);
    };

    const popupClassName = [
        selectProps.popupClassName,
        expandLabels ? 'ps-expanded-labels' : '',
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <LabelExpandContext.Provider value={expandLabels}>
        <Select
            ref={ref}
            showSearch
            filterOption={false}
            searchValue={search}
            onSearch={setSearch}
            options={displayedOptions}
            onPopupScroll={onPopupScroll}
            {...selectProps}
            popupClassName={popupClassName || undefined}
            onChange={onChange}
            onDropdownVisibleChange={onDropdownVisibleChange}
            dropdownRender={(menu) => (
                <>
                    {showFullLabelToggle && (
                        <div
                            // keep the click inside the popup: without this the
                            // select loses focus and the dropdown closes
                            onMouseDown={(e) => e.preventDefault()}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 8,
                                padding: '6px 10px',
                                borderBottom: '1px solid #f0f0f0',
                            }}
                        >
                            <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                                {fullLabelToggleText}
                            </span>
                            <Switch
                                size="small"
                                checked={expandLabels}
                                onChange={setExpandLabels}
                            />
                        </div>
                    )}
                    {menu}
                    {loading && (
                        <div style={{ textAlign: 'center', padding: '8px 0' }}>
                            <Circular size={14} color={colorPrimary} />
                        </div>
                    )}
                    <ScrollEndMessage
                        hasMore={hasMore}
                        loading={loading}
                        dataLength={displayedOptions.length}
                        message={endMessage}
                        style={{ padding: '6px 8px', fontSize: 12 }}
                    />
                </>
            )}
        />
        </LabelExpandContext.Provider>
    );
});

export default PaginatedSelect;
